import React, { useMemo, useState } from 'react';

const favicon = (domain, sz = 64) => `https://www.google.com/s2/favicons?domain=${domain}&sz=${sz}`;
const placehold = (letter, size = '20x20') => `https://placehold.co/${size}/0f172a/ffffff?text=${letter}`;

const LAYERS = {
  1: {
    title: 'Layer 1: Clinical Operations & Trial Intake',
    desc: 'Accelerates the clinical pipeline by bypassing physical site bottlenecks and eliminating manual CRO contracting friction.',
    regulatory: "Strategic Pathway: Execute via the 'Predictive Trial Setup' and 'Patient-Centric DCT Platform' programs mapped in Tab 3.",
    stat: 'Velocity (Trial Speed)',
    icon: 'fa-solid fa-stopwatch',
    type: 'Layer Analysis',
  },
  2: {
    title: 'Layer 2: Sovereign API & Cold Chain Logistics',
    desc: 'Secures biological supply lines by shifting dependency away from volatile jurisdictions and tracking cryogenic metrics.',
    regulatory: "Strategic Pathway: Execute via the 'Closed-Loop Biologics Assurance' and 'Sovereign API Redundancy' programs mapped in Tab 3.",
    stat: 'Resilience (Supply Security)',
    icon: 'fa-solid fa-temperature-arrow-down',
    type: 'Layer Analysis',
  },
  3: {
    title: 'Layer 3: Bio-Digital Bill of Materials (SDBOM)',
    desc: 'Bridges the physical-digital divide by version-controlling hardware devices directly alongside active software models and firmware.',
    regulatory: "Strategic Pathway: Execute via the 'Digital Product Thread' and 'Biological Ingredient Traceability' programs mapped in Tab 3.",
    stat: 'Integration (Product Integrity)',
    icon: 'fa-solid fa-microchip',
    type: 'Layer Analysis',
  },
  4: {
    title: 'Layer 4: Sovereign Data & Regulatory Governance',
    desc: 'Builds an absolute compliance shield around patient data pipelines and models to protect enterprise IP from legal EU deletion.',
    regulatory: "Strategic Pathway: Execute via the 'Federated Health Data Enclaves' and 'Sovereign AI Trust Shield' programs mapped in Tab 3.",
    stat: 'Defense (Valuation Shield)',
    icon: 'fa-solid fa-shield-halved',
    type: 'Layer Analysis',
  },
};

/* ─── Player detail data (Tab 2 sidebar) ─── */
const PLAYERS = {
  veeva: { title: 'Veeva Systems', desc: 'The definitive cloud platform for global life sciences clinical operations and regulatory compliance.', regulatory: 'EU Annex 11 / 21 CFR Part 11 Compliant.', stat: 'Market Defender', icon: 'fa-solid fa-building', type: 'Vendor Profile' },
  science37: { title: 'Science 37', desc: 'Pioneering the operating system for decentralized clinical trials (DCTs) to accelerate remote cohort onboarding.', regulatory: 'GDPR / HIPAA Compliant Patient Portals.', stat: 'AI Insurgent', icon: 'fa-solid fa-rocket', type: 'Vendor Profile' },
  tracelink: { title: 'TraceLink', desc: 'Digital supply network executing end-to-end pharmaceutical ingredient tracking and serialization.', regulatory: 'FMD / DSCSA Track & Trace Baseline.', stat: 'Market Defender', icon: 'fa-solid fa-building', type: 'Vendor Profile' },
  cryoport: { title: 'Cryoport', desc: 'The definitive champion of deep cryogenic temperature tracking for advanced cell and gene therapies.', regulatory: 'GDP / ISO 21011 Cryogenic Standards.', stat: 'AI Insurgent', icon: 'fa-solid fa-rocket', type: 'Vendor Profile' },
  benchling: { title: 'Benchling', desc: 'The leading R&D cloud software bridging complex biological workflows and structural data management.', regulatory: 'GxP / GLP Lab Environment Ready.', stat: 'Market Defender', icon: 'fa-solid fa-building', type: 'Vendor Profile' },
  scilife: { title: 'Scilife', desc: 'A purpose-built, GAMP 5-aligned electronic Quality Management System (eQMS) for European life sciences and SaMD.', regulatory: 'GAMP 5 / ISO 13485 / MDR Aligned.', stat: 'AI Insurgent', icon: 'fa-solid fa-rocket', type: 'Vendor Profile' },
  owkin: { title: 'Owkin', desc: 'The gold standard for federated AI in oncology, training models inside hospital walls without moving patient data.', regulatory: 'EHDS Ready / Zero-Trust Architecture.', stat: 'AI Insurgent', icon: 'fa-solid fa-rocket', type: 'Vendor Profile' },
  scale: { title: 'Scale AI Health', desc: 'Providing curated, high-fidelity data labeling and model validation pipelines for life science algorithms.', regulatory: 'Strict Model Pedigree / Lineage Tracing.', stat: 'AI Insurgent', icon: 'fa-solid fa-rocket', type: 'Vendor Profile' },
};

/* ─── Tab 3: Capability filter buttons ─── */
const CAP_FILTERS_DEFENSE = [
  { tag: 'velocity-barrier', label: 'Velocity Barriers', icon: 'fa-solid fa-stopwatch text-orange-400/80', hover: 'hover:border-orange-500' },
  { tag: 'regulatory-bottleneck', label: 'Regulatory Bottlenecks', icon: 'fa-solid fa-triangle-exclamation text-rose-400/80', hover: 'hover:border-rose-500' },
  { tag: 'geopolitical-risk', label: 'Geopolitical Risks', icon: 'fa-solid fa-earth-americas text-amber-400/80', hover: 'hover:border-amber-500' },
  { tag: 'capacity-bottleneck', label: 'Capacity Bottlenecks', icon: 'fa-solid fa-cubes-stacked text-pink-400/80', hover: 'hover:border-pink-500' },
  { tag: 'cost-leakage', label: 'Cost Leakage', icon: 'fa-solid fa-money-bill-trend-up text-yellow-400/80', hover: 'hover:border-yellow-500' },
];

const CAP_FILTERS_OFFENSE = [
  { tag: 'agentic-ai', label: 'Agentic AI', icon: 'fa-solid fa-bolt text-indigo-400/80', hover: 'hover:border-indigo-500' },
  { tag: 'real-time-telemetry', label: 'Real-Time Telemetry', icon: 'fa-solid fa-chart-line text-cyan-400/80', hover: 'hover:border-cyan-500' },
  { tag: 'sovereign-control', label: 'Sovereign Control', icon: 'fa-solid fa-shield-halved text-emerald-400/80', hover: 'hover:border-emerald-500' },
  { tag: 'predictive-analytics', label: 'Predictive Analytics', icon: 'fa-solid fa-wand-magic-sparkles text-blue-400/80', hover: 'hover:border-blue-500' },
  { tag: 'digital-integration', label: 'Digital Integration', icon: 'fa-solid fa-microchip text-violet-400/80', hover: 'hover:border-violet-500' },
];

const MATURITY_FILTERS = [
  { val: 'emerging', label: 'Emerging Capabilities', icon: 'fa-solid fa-seedling text-violet-400/85', hover: 'hover:border-violet-500' },
  { val: 'growth', label: 'Growth Capabilities', icon: 'fa-solid fa-chart-line text-emerald-400/85', hover: 'hover:border-emerald-500' },
  { val: 'mature', label: 'Mature Capabilities', icon: 'fa-solid fa-check-double text-blue-400/85', hover: 'hover:border-blue-500' },
];

/* ─── Tab 3: Capability taxonomy cards ─── */
const TAG_CHIP = {
  'sovereign-control': { label: 'Sovereign Control', cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  'regulatory-bottleneck': { label: 'Reg Bottleneck', cls: 'bg-rose-500/10 text-rose-400 border-rose-500/20' },
  'agentic-ai': { label: 'Agentic AI', cls: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' },
  'digital-integration': { label: 'Digital Integration', cls: 'bg-violet-500/10 text-violet-400 border-violet-500/20' },
  'cost-leakage': { label: 'Cost Leakage', cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' },
  'capacity-bottleneck': { label: 'Capacity Bottleneck', cls: 'bg-pink-500/10 text-pink-400 border-pink-500/20' },
  'real-time-telemetry': { label: 'Real-Time Telemetry', cls: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' },
  'geopolitical-risk': { label: 'Geopolitical Risk', cls: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  'predictive-analytics': { label: 'Predictive Analytics', cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  'velocity-barrier': { label: 'Velocity Barrier', cls: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
};

const MATURITY_CHIP = {
  growth: { label: 'Growth', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  emerging: { label: 'Emerging', cls: 'bg-violet-500/15 text-violet-300 border-violet-500/30' },
  mature: { label: 'Mature', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
};

const CAP_LAYERS = [
  {
    layer: 4,
    accent: 'rose',
    borderL: 'border-l-rose-500',
    shadow: 'shadow-rose-500/5',
    numCls: 'bg-rose-500/10 border-rose-500/20 text-rose-400',
    titleCls: 'text-rose-400',
    title: 'Layer 4: Sovereign Data & Regulatory Governance',
    subtitle: 'Enterprise Defense & Compliance Shield',
    cols: 'md:grid-cols-3',
    cards: [
      { title: 'Federated Health Data Enclaves (EHDS Transition)', tags: ['sovereign-control', 'regulatory-bottleneck'], maturity: 'growth', desc: 'Establishing secure, compliant data environments to leverage and monetize real-world patient registries and clinical trial data under European EHDS & GDPR secondary use frameworks.' },
      { title: 'Sovereign AI Compliance & Trust Shield', tags: ['sovereign-control', 'regulatory-bottleneck'], maturity: 'emerging', desc: 'A unified auditing engine ensuring diagnostic models and algorithms maintain continuous bias-monitoring, data lineage tracing, and GxP alignment to pass strict EU AI Act gates.' },
      { title: 'Dynamic Patient-Consent Infrastructure', tags: ['agentic-ai', 'regulatory-bottleneck'], maturity: 'emerging', desc: 'An agentic system of record that instantaneously maps, updates, and propagates patient consent-withdrawals across downstream database tables, preventing illegal training runs.' },
    ],
  },
  {
    layer: 3,
    accent: 'emerald',
    borderL: 'border-l-emerald-500',
    shadow: 'shadow-emerald-500/5',
    numCls: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    titleCls: 'text-emerald-400',
    title: 'Layer 3: Bio-Digital Bill of Materials (SDBOM)',
    subtitle: 'Physical-Digital Composition Alignment',
    cols: 'md:grid-cols-2',
    cards: [
      { title: 'Digital Product Thread (SDBOM) Integration', tags: ['digital-integration', 'cost-leakage'], maturity: 'growth', desc: 'Building a singular digital thread that links, version-controls, and tracks physical medical device architectures directly alongside their active software models and firmware updates.' },
      { title: 'Biological Ingredient & Batch Traceability', tags: ['digital-integration', 'capacity-bottleneck'], maturity: 'mature', desc: 'Synchronizing molecular/chemical recipes, active ingredients (APIs), and biological batch records (viral vectors) with electronic quality release (eBR) systems to ensure absolute provenance.' },
    ],
  },
  {
    layer: 2,
    accent: 'indigo',
    borderL: 'border-l-indigo-500',
    shadow: 'shadow-indigo-500/5',
    numCls: 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400',
    titleCls: 'text-indigo-400',
    title: 'Layer 2: Sovereign API & Cold Chain Logistics',
    subtitle: 'Physical Supply Chain Integrity & Resilience',
    cols: 'md:grid-cols-2',
    cards: [
      { title: 'Closed-Loop Biologics Cold Chain Assurance', tags: ['real-time-telemetry', 'capacity-bottleneck'], maturity: 'growth', desc: 'Deploying end-to-end cryogenic telemetry (down to -196°C) paired with unbroken Chain-of-Identity (COI) tracking, securing the biological integrity of cell therapies from patient back to patient.' },
      { title: 'Sovereign API & Nearshore Sourcing Redundancy', tags: ['geopolitical-risk', 'cost-leakage'], maturity: 'growth', desc: 'An algorithmic supply chain hedge that automatically shifts and maps raw chemical precursor lines away from volatile geopolitical jurisdictions and into vetted European backup facilities.' },
    ],
  },
  {
    layer: 1,
    accent: 'teal',
    borderL: 'border-l-teal-500',
    shadow: 'shadow-teal-500/5',
    numCls: 'bg-teal-500/10 border-teal-500/20 text-teal-400',
    titleCls: 'text-teal-400',
    title: 'Layer 1: Clinical Operations & Trial Intake',
    subtitle: 'Sourcing Speed & Trial Site Acquisition',
    cols: 'md:grid-cols-2',
    cards: [
      { title: 'Predictive Trial Setup & Feasibility Simulation', tags: ['predictive-analytics', 'velocity-barrier'], maturity: 'growth', desc: 'Running advanced protocol simulations against synthetic historical clinical databases to identify site bottlenecks, optimize recruitment models, and match high-performing trial investigators.' },
      { title: 'Patient-Centric DCT (Decentralized Trial) Platform', tags: ['digital-integration', 'velocity-barrier'], maturity: 'mature', desc: 'Unifying remote patient screening, e-consent, and localized tracking portals to bypass physical site bottlenecks and accelerate cohort onboarding velocity.' },
    ],
  },
];

const CAP_HOVER = { 4: 'hover:border-rose-500/40', 3: 'hover:border-emerald-500/40', 2: 'hover:border-indigo-500/40', 1: 'hover:border-teal-500/40' };
const CAP_ACTIVE_BORDER = { 4: 'border-rose-500/60', 3: 'border-emerald-500/60', 2: 'border-indigo-500/60', 1: 'border-teal-500/60' };

/* ─── Tab 4: Target Discovery data ─── */
const INSURGENT_BORDER = { rose: 'border-rose-500/30', emerald: 'border-emerald-500/30', indigo: 'border-indigo-500/30', teal: 'border-teal-500/30' };
const BADGE_CLS = {
  rose: 'border-rose-500/30 bg-rose-500/10 text-rose-300',
  emerald: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
  indigo: 'border-indigo-500/30 bg-indigo-500/10 text-indigo-300',
  teal: 'border-teal-500/30 bg-teal-500/10 text-teal-300',
  slate: 'border-slate-700 bg-slate-800 text-slate-400',
};

const DISCOVERY_LAYERS = [
  {
    id: 'L4',
    title: 'Sovereign Data & Regulatory Governance',
    color: 'rose',
    chipCls: 'bg-rose-600/20 text-rose-500 border-rose-500/30 shadow-rose-600/10',
    dotCls: 'bg-rose-500',
    grid: 'grid grid-cols-1 xl:grid-cols-3 gap-5',
    segments: [
      {
        title: '1. Federated Health Data Enclaves',
        defenders: [
          { name: 'IQVIA', domain: 'iqvia.com', letter: 'I', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'LEGACY', role: 'PLATFORM' },
          { name: 'Oracle Health', domain: 'oracle.com', letter: 'O', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'LEGACY', role: 'PLATFORM' },
        ],
        insurgents: [
          { name: 'Owkin', domain: 'owkin.com', letter: 'O', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'PLATFORM', badge: 'rose' },
          { name: 'TriNetX', domain: 'trinetx.com', letter: 'T', geo: 'AMERICAS', ownership: 'VC-BACKED', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM' },
          { name: 'Aetion', domain: 'aetion.com', letter: 'A', geo: 'AMERICAS', ownership: 'VC-BACKED', pedigree: 'CLOUD-NATIVE', role: 'SPECIALIST' },
          { name: 'Apheris', domain: 'apheris.com', letter: 'A', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'PLATFORM', badge: 'rose' },
          { name: 'Bitfount', domain: 'bitfount.com', letter: 'B', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'PLATFORM', badge: 'rose' },
        ],
      },
      {
        title: '2. Sovereign AI Compliance Shield',
        defenders: [
          { name: 'Veeva Systems', domain: 'veeva.com', letter: 'V', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM' },
          { name: 'ArisGlobal', domain: 'arisglobal.com', letter: 'A', geo: 'AMERICAS', ownership: 'PRIVATE', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM' },
        ],
        insurgents: [
          { name: 'WhizAI', domain: 'whiz.ai', letter: 'W', geo: 'AMERICAS', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'SPECIALIST' },
          { name: 'Credo AI', domain: 'credo.ai', letter: 'C', geo: 'AMERICAS', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'SPECIALIST' },
          { name: 'LatticeFlow AI', domain: 'latticeflow.ai', letter: 'L', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'SPECIALIST', badge: 'rose' },
          { name: 'Holistic AI', domain: 'holisticai.com', letter: 'H', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'SPECIALIST', badge: 'rose' },
        ],
      },
      {
        title: '3. Dynamic Patient-Consent',
        defenders: [
          { name: 'Medidata', domain: 'medidata.com', letter: 'M', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM' },
        ],
        insurgents: [
          { name: 'Castor', domain: 'castoredc.com', letter: 'C', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'CLOUD-NATIVE', role: 'SPECIALIST', badge: 'rose' },
          { name: 'Medable Consent', domain: 'medable.com', letter: 'M', geo: 'AMERICAS', ownership: 'VC-BACKED', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM' },
        ],
      },
    ],
  },
  {
    id: 'L3',
    title: 'Bio-Digital Bill of Materials (SDBOM)',
    color: 'emerald',
    chipCls: 'bg-emerald-600/20 text-emerald-500 border-emerald-500/30 shadow-emerald-600/10',
    dotCls: 'bg-emerald-500',
    grid: 'grid grid-cols-1 lg:grid-cols-2 gap-5',
    segments: [
      {
        title: '4. Digital Product Thread (SDBOM)',
        defenders: [
          { name: 'Siemens Healthineers', domain: 'siemens.com', letter: 'S', geo: 'EMEA', ownership: 'PUBLIC', pedigree: 'LEGACY', role: 'PLATFORM' },
          { name: 'PTC Windchill', domain: 'ptc.com', letter: 'P', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'LEGACY', role: 'PLATFORM' },
          { name: 'Dassault Biovia', domain: '3ds.com', letter: 'D', geo: 'EMEA', ownership: 'PUBLIC', pedigree: 'LEGACY', role: 'PLATFORM' },
        ],
        insurgents: [
          { name: 'Matrix Requirements', domain: 'matrixreq.com', letter: 'M', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'CLOUD-NATIVE', role: 'SPECIALIST', badge: 'emerald' },
          { name: 'Propel Software', domain: 'propelsoftware.com', letter: 'P', geo: 'AMERICAS', ownership: 'VC-BACKED', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM' },
          { name: 'Scilife', domain: 'scilife.io', letter: 'S', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM', badge: 'emerald' },
        ],
      },
      {
        title: '5. Biological Ingredient Traceability',
        defenders: [
          { name: 'SAP ATTP', domain: 'sap.com', letter: 'S', geo: 'EMEA', ownership: 'PUBLIC', pedigree: 'LEGACY', role: 'PLATFORM' },
          { name: 'Rockwell Automation', domain: 'rockwellautomation.com', letter: 'R', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'LEGACY', role: 'PLATFORM' },
          { name: 'Körber Pharma', domain: 'koerber-pharma.com', letter: 'K', geo: 'EMEA', ownership: 'PRIVATE', pedigree: 'LEGACY', role: 'PLATFORM', badge: 'slate' },
        ],
        insurgents: [
          { name: 'Apprentice.io', domain: 'apprentice.io', letter: 'A', geo: 'AMERICAS', ownership: 'VC-BACKED', pedigree: 'CLOUD-NATIVE', role: 'SPECIALIST' },
          { name: 'Tulip Interfaces', domain: 'tulip.co', letter: 'T', geo: 'AMERICAS', ownership: 'VC-BACKED', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM' },
          { name: 'TraceLink', domain: 'tracelink.com', letter: 'T', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM' },
        ],
      },
    ],
  },
  {
    id: 'L2',
    title: 'Sovereign API & Cold Chain Logistics',
    color: 'indigo',
    chipCls: 'bg-indigo-600/20 text-indigo-400 border-indigo-500/30 shadow-indigo-600/10',
    dotCls: 'bg-indigo-500',
    grid: 'grid grid-cols-1 lg:grid-cols-2 gap-5',
    segments: [
      {
        title: '6. Closed-Loop Biologics Assurance',
        defenders: [
          { name: 'FedEx Custom Critical', domain: 'fedex.com', letter: 'F', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'LEGACY', role: 'SPECIALIST' },
          { name: 'Thermo Fisher', domain: 'thermofisher.com', letter: 'T', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'LEGACY', role: 'PLATFORM' },
          { name: 'ELPRO', domain: 'elpro.com', letter: 'E', geo: 'EMEA', ownership: 'PRIVATE', pedigree: 'LEGACY', role: 'SPECIALIST', badge: 'slate' },
        ],
        insurgents: [
          { name: 'Cryoport', domain: 'cryoport.com', letter: 'C', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'CLOUD-NATIVE', role: 'SPECIALIST' },
          { name: 'Controlant', domain: 'controlant.com', letter: 'C', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM', badge: 'indigo' },
          { name: 'SkyCell', domain: 'skycell.ch', letter: 'S', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'CLOUD-NATIVE', role: 'SPECIALIST', badge: 'indigo' },
        ],
      },
      {
        title: '7. Sovereign API Redundancy',
        defenders: [
          { name: 'McKinsey QuantumBlack', domain: 'mckinsey.com', letter: 'M', geo: 'AMERICAS', ownership: 'PRIVATE', pedigree: 'AI-NATIVE', role: 'SPECIALIST' },
          { name: 'Euroapi', domain: 'euroapi.com', letter: 'E', geo: 'EMEA', ownership: 'PUBLIC', pedigree: 'LEGACY', role: 'PLATFORM', badge: 'slate' },
        ],
        insurgents: [
          { name: 'Resilinc', domain: 'resilinc.com', letter: 'R', geo: 'AMERICAS', ownership: 'VC-BACKED', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM' },
          { name: 'Everstream Analytics', domain: 'everstream.ai', letter: 'E', geo: 'AMERICAS', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'PLATFORM' },
          { name: 'Kemiex', domain: 'kemiex.com', letter: 'K', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM', badge: 'indigo' },
        ],
      },
    ],
  },
  {
    id: 'L1',
    title: 'Clinical Operations & Trial Intake',
    color: 'teal',
    chipCls: 'bg-teal-600/20 text-teal-400 border-teal-500/30 shadow-teal-600/10',
    dotCls: 'bg-teal-500',
    grid: 'grid grid-cols-1 lg:grid-cols-2 gap-5',
    segments: [
      {
        title: '8. Predictive Trial Setup',
        defenders: [
          { name: 'IQVIA', domain: 'iqvia.com', letter: 'I', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'LEGACY', role: 'PLATFORM' },
          { name: 'Medidata AI', domain: 'medidata.com', letter: 'M', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM' },
        ],
        insurgents: [
          { name: 'H1', domain: 'h1.co', letter: 'H', geo: 'AMERICAS', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'PLATFORM' },
          { name: 'Lokavant', domain: 'lokavant.com', letter: 'L', geo: 'AMERICAS', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'SPECIALIST' },
          { name: 'Novadiscovery', domain: 'novadiscovery.com', letter: 'N', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'SPECIALIST', badge: 'teal' },
        ],
      },
      {
        title: '9. Patient-Centric DCT Platform',
        defenders: [
          { name: 'Science 37', domain: 'science37.com', letter: 'S', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM' },
          { name: 'Veeva Systems', domain: 'veeva.com', letter: 'V', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM' },
        ],
        insurgents: [
          { name: 'Medable DCT', domain: 'medable.com', letter: 'M', geo: 'AMERICAS', ownership: 'VC-BACKED', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM' },
          { name: 'THREAD', domain: 'threadresearch.com', letter: 'T', geo: 'AMERICAS', ownership: 'VC-BACKED', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM' },
          { name: 'ObvioHealth', domain: 'obviohealth.com', letter: 'O', geo: 'AMERICAS', ownership: 'VC-BACKED', pedigree: 'CLOUD-NATIVE', role: 'SPECIALIST' },
          { name: 'Huma', domain: 'huma.com', letter: 'H', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM', badge: 'teal' },
        ],
      },
    ],
  },
];

const DISC_FILTER_GROUPS = [
  { key: 'geo', label: 'Geopolitical Bloc', icon: 'fa-solid fa-globe', options: ['ALL', 'AMERICAS', 'EMEA', 'APAC'], labels: { ALL: 'All', AMERICAS: 'Americas', EMEA: 'EMEA', APAC: 'APAC' } },
  { key: 'ownership', label: 'Ownership Structure', icon: 'fa-solid fa-building', options: ['ALL', 'PUBLIC', 'VC-BACKED'], labels: { ALL: 'All', PUBLIC: 'Public', 'VC-BACKED': 'VC-Backed' } },
  { key: 'pedigree', label: 'Tech Pedigree', icon: 'fa-solid fa-code-branch', options: ['ALL', 'AI-NATIVE', 'LEGACY'], labels: { ALL: 'All', 'AI-NATIVE': 'AI-Native', LEGACY: 'Legacy' } },
  { key: 'role', label: 'Structural Role', icon: 'fa-solid fa-layer-group', options: ['ALL', 'PLATFORM', 'SPECIALIST'], labels: { ALL: 'All', PLATFORM: 'Platform', SPECIALIST: 'Specialist' } },
];

const MAIN_TABS = [
  { id: 1, label: '1. Strategic Context', icon: 'fa-solid fa-compass-drafting' },
  { id: 2, label: '2. Sourcing Stack', icon: 'fa-solid fa-network-wired' },
  { id: 3, label: '3. Interventions', icon: 'fa-solid fa-layer-group' },
  { id: 4, label: '4. Target Discovery', icon: 'fa-solid fa-radar' },
  { id: 5, label: '5. Co-Execution', icon: 'fa-solid fa-infinity' },
];

/* ─── Small render helpers ─── */
const TargetChip = ({ t, layerColor, visible }) => (
  <div
    className={`flex items-center gap-2.5 p-2 px-3 bg-[#191e2e] border ${t.type === 'insurgent' ? INSURGENT_BORDER[layerColor] : 'border-[#2a324a]'} rounded-xl transition-all`}
    style={{ display: visible ? 'flex' : 'none' }}
  >
    <img
      src={favicon(t.domain)}
      alt=""
      className="w-5 h-5 rounded bg-white p-0.5"
      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = placehold(t.letter); }}
    />
    <span className="text-xs font-semibold text-slate-200">{t.name}</span>
    {t.badge && <span className={`text-[8px] px-1.5 py-0.5 rounded border ${BADGE_CLS[t.badge]}`}>EMEA</span>}
  </div>
);

const PlayerChip = ({ playerKey, domain, letter, name, onSelect }) => (
  <span
    onClick={(e) => { e.stopPropagation(); onSelect(playerKey); }}
    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-950 hover:bg-slate-800 text-[11px] font-medium text-slate-200 border border-slate-800 rounded-lg transition-all"
  >
    <img
      src={favicon(domain, 32)}
      alt=""
      className="w-3.5 h-3.5 rounded"
      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = placehold(letter, '16x16'); }}
    />
    {name}
  </span>
);

export default function LifeSciencesAnalysis() {
  /* Cover / app transition */
  const [coverVisible, setCoverVisible] = useState(true);
  const [coverOpaque, setCoverOpaque] = useState(true);
  const [appVisible, setAppVisible] = useState(false);
  const [appOpaque, setAppOpaque] = useState(false);

  /* Tabs */
  const [activeTab, setActiveTab] = useState(1);

  /* Tab 2: layer / player detail */
  const [selectedLayer, setSelectedLayer] = useState(1);
  const [detail, setDetail] = useState(LAYERS[1]);

  /* Tab 3: capability filters */
  const [capFilter, setCapFilter] = useState('ALL');
  const [maturityFilter, setMaturityFilter] = useState('ALL');

  /* Tab 4: discovery filters */
  const [discFilters, setDiscFilters] = useState({ geo: 'ALL', ownership: 'ALL', pedigree: 'ALL', role: 'ALL' });

  const launchEngine = () => {
    setCoverOpaque(false);
    setTimeout(() => {
      setCoverVisible(false);
      setAppVisible(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setAppOpaque(true)));
    }, 1000);
  };

  const returnToCover = () => {
    setAppOpaque(false);
    setTimeout(() => {
      setAppVisible(false);
      setCoverVisible(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setCoverOpaque(true)));
    }, 1000);
  };

  const selectLayer = (id) => {
    setSelectedLayer(id);
    setDetail(LAYERS[id]);
  };

  const selectPlayer = (key) => {
    setDetail(PLAYERS[key]);
  };

  const toggleCapFilter = (tag) => setCapFilter((cur) => (cur === tag ? 'ALL' : tag));
  const toggleMaturityFilter = (val) => setMaturityFilter((cur) => (cur === val ? 'ALL' : val));
  const setDiscFilter = (category, val) => setDiscFilters((f) => ({ ...f, [category]: val }));

  const matchTarget = (t) =>
    (discFilters.geo === 'ALL' || t.geo === discFilters.geo) &&
    (discFilters.ownership === 'ALL' || t.ownership === discFilters.ownership) &&
    (discFilters.pedigree === 'ALL' || t.pedigree === discFilters.pedigree) &&
    (discFilters.role === 'ALL' || t.role === discFilters.role);

  const matchCount = useMemo(() => {
    let count = 0;
    DISCOVERY_LAYERS.forEach((l) =>
      l.segments.forEach((s) => {
        s.defenders.forEach((t) => { if (matchTarget(t)) count++; });
        s.insurgents.forEach((t) => { if (matchTarget(t)) count++; });
      })
    );
    return count;
  }, [discFilters]);

  const capCardClass = (card, layer) => {
    const matches =
      (capFilter === 'ALL' || card.tags.includes(capFilter)) &&
      (maturityFilter === 'ALL' || card.maturity === maturityFilter);
    const narrowed = capFilter !== 'ALL' || maturityFilter !== 'ALL';
    const border = matches && narrowed ? CAP_ACTIVE_BORDER[layer] : 'border-slate-800/60';
    const opacity = matches ? 'opacity-100' : 'opacity-20';
    return `bg-slate-950/80 p-6 border ${border} ${opacity} rounded-xl ${CAP_HOVER[layer]} transition-all duration-300 group flex flex-col`;
  };

  const layerBoxClass = (id) => {
    if (selectedLayer === id) {
      const accent = { 1: 'border-teal-500 shadow-teal-500/10', 2: 'border-indigo-500 shadow-indigo-500/10', 3: 'border-emerald-500 shadow-emerald-500/10', 4: 'border-rose-500 shadow-rose-500/10' }[id];
      return `group bg-slate-900 border-2 ${accent} rounded-xl p-5 cursor-pointer transition-all-300 translate-x-1 relative overflow-hidden shadow-lg`;
    }
    const hover = { 1: 'hover:border-teal-500/40', 2: 'hover:border-indigo-500/40', 3: 'hover:border-emerald-500/40', 4: 'hover:border-rose-500/40' }[id];
    return `group bg-slate-900 border border-slate-800 rounded-xl p-5 cursor-pointer transition-all-300 ${hover} hover:translate-x-1 relative overflow-hidden`;
  };

  const tabBtnClass = (id) => {
    const base = 'px-4 py-4 rounded-xl text-[11px] sm:text-xs tracking-wider uppercase transition-all-300 flex items-center justify-center gap-2';
    const span5 = id === 5 ? 'col-span-2 lg:col-span-1 ' : '';
    if (activeTab === id) {
      return `${span5}${base} font-extrabold bg-teal-500/10 border-2 border-teal-500 text-teal-400 shadow-lg shadow-teal-500/5`;
    }
    return `${span5}${base} font-bold bg-slate-900/40 border border-slate-800/60 text-slate-400 hover:text-white`;
  };

  const sectionClass = (id) => {
    if (activeTab !== id) return 'hidden';
    return id === 4 ? 'space-y-6 block animate-fade-in' : 'space-y-8 block animate-fade-in';
  };

  const capFilterBtnClass = (f) => {
    if (capFilter === f.tag) {
      return 'flex items-center gap-2 px-3.5 py-2 bg-teal-500/15 border-2 border-teal-500 text-teal-400 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all shadow-lg shadow-teal-500/5';
    }
    return `flex items-center gap-2 px-3 py-2 bg-slate-800/50 border border-slate-700 ${f.hover} text-slate-400 hover:text-white text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all`;
  };

  const maturityFilterBtnClass = (f) => {
    if (maturityFilter === f.val) {
      return 'flex items-center gap-2 px-3.5 py-2 bg-violet-500/15 border-2 border-violet-500 text-violet-400 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all shadow-lg shadow-violet-500/5';
    }
    return `flex items-center gap-2 px-3.5 py-2 bg-slate-800/50 border border-slate-700 ${f.hover} text-slate-400 hover:text-white text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all`;
  };

  const discBtnClass = (category, val) => {
    const base = `btn-filter-${category} px-2.5 py-1.5 text-[9px] font-bold uppercase tracking-wider rounded-md transition-all`;
    if (discFilters[category] === val) return `${base} bg-slate-800 text-white border border-slate-600`;
    return `${base} text-slate-400 border border-transparent hover:text-slate-200`;
  };

  return (
    <div className="bg-slate-950 text-slate-100 antialiased min-h-screen selection:bg-teal-500 selection:text-black" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Fonts & icon stylesheets (hoisted to <head> by React 19) */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" precedence="default" />
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" precedence="default" />

      <style>{`
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #030712; }
        ::-webkit-scrollbar-thumb { background: #1f2937; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #374151; }
        .glow-teal { box-shadow: 0 0 25px -5px rgba(45, 212, 191, 0.15); }
        .glow-indigo { box-shadow: 0 0 25px -5px rgba(99, 102, 241, 0.15); }
        .glow-emerald { box-shadow: 0 0 25px -5px rgba(16, 185, 129, 0.15); }
        .glow-rose { box-shadow: 0 0 25px -5px rgba(244, 63, 94, 0.15); }
        .transition-all-300 { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
      `}</style>

      {/* ==================== COVER PAGE (LAUNCH SCREEN) ==================== */}
      <div
        className={`fixed inset-0 z-[100] bg-slate-950 flex-col items-center justify-center transition-opacity duration-1000 ${coverVisible ? 'flex' : 'hidden'} ${coverOpaque ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        {/* Ambient Background Glow */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-tr from-cyan-500/20 via-blue-500/5 to-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
        </div>

        <div className="relative z-10 text-center space-y-10 px-4 max-w-4xl mx-auto flex flex-col items-center">
          {/* DJA Brand Icon */}
          <div className="w-20 h-20 bg-slate-900 border border-slate-800 rounded-2xl flex items-center justify-center shadow-2xl relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/20 to-purple-600/20 opacity-50"></div>
            <i className="fa-solid fa-dna text-4xl text-transparent bg-clip-text bg-gradient-to-tr from-cyan-400 to-purple-500 relative z-10"></i>
          </div>

          {/* Core Typography */}
          <div className="space-y-6">
            <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-slate-500 block">Confidential Boardroom Briefing</span>

            <h1 className="text-4xl sm:text-5xl md:text-7xl font-black tracking-tight text-white leading-tight">
              Procurement Transformation <br className="hidden lg:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500">Alpha Engine</span>
            </h1>

            <p className="text-sm sm:text-base text-slate-400 font-medium tracking-wide max-w-2xl mx-auto leading-relaxed">
              European Life Sciences Sourcing & Capability Map. <br className="hidden sm:block" />A unified matrix for sovereign M&A targeting, clinical velocity, and compliance auditing.
            </p>
          </div>

          {/* Launch Action */}
          <div className="pt-8">
            <button
              onClick={launchEngine}
              className="group relative px-8 py-4 bg-slate-950/50 border border-slate-700 hover:border-cyan-500/50 rounded-full overflow-hidden transition-all duration-500 focus:outline-none shadow-[0_0_40px_-10px_rgba(45,212,191,0.1)] hover:shadow-[0_0_40px_-10px_rgba(45,212,191,0.3)]"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 translate-y-[100%] group-hover:translate-y-0 transition-transform duration-500 ease-out"></div>
              <span className="relative z-10 flex items-center gap-3 text-xs font-bold text-slate-300 group-hover:text-white uppercase tracking-[0.2em] transition-colors">
                Initialize System <i className="fa-solid fa-fingerprint text-cyan-400 group-hover:text-purple-400 transition-colors"></i>
              </span>
            </button>
          </div>
        </div>

        {/* Corporate Meta Footer */}
        <div className="absolute bottom-8 left-0 right-0 text-center text-[9px] text-slate-600 font-mono tracking-widest uppercase flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
          <span>DJA Frontier IP</span>
          <span className="hidden sm:block w-1 h-1 rounded-full bg-slate-700"></span>
          <span>Version 2026.1</span>
          <span className="hidden sm:block w-1 h-1 rounded-full bg-slate-700"></span>
          <span>Abu Dhabi / Luxembourg</span>
        </div>
      </div>

      {/* ==================== MAIN APPLICATION CONTAINER ==================== */}
      <div className={`${appVisible ? '' : 'hidden'} ${appOpaque ? 'opacity-100' : 'opacity-0 pointer-events-none'} transition-opacity duration-1000 pb-20`}>
        <header className="border-b border-slate-800 bg-slate-900/40 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
            {/* Clickable Logo/Branding */}
            <div className="flex items-center gap-4 cursor-pointer group" onClick={returnToCover} title="Return to Launch Screen">
              <div className="bg-gradient-to-tr from-cyan-400 to-purple-600 p-2.5 rounded-xl shadow-lg shadow-cyan-500/20 group-hover:shadow-cyan-500/40 transition-all duration-300">
                <i className="fa-solid fa-dna text-white text-xl group-hover:scale-110 transition-transform duration-300"></i>
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-400">Life Sciences Sourcing & Capability Map</span>
                <h1 className="text-xl sm:text-2xl font-black tracking-tight text-white group-hover:text-slate-200 transition-colors duration-300">
                  DJA Frontier <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500">Decision Engine</span>
                </h1>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 space-y-8">
          {/* Master Presentation Tabs Bar */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 border-b border-slate-800/80 pb-4">
            {MAIN_TABS.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={tabBtnClass(tab.id)}>
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {/* ==================== TAB 1 CONTENT: STRATEGIC CONTEXT ==================== */}
          <section className={sectionClass(1)}>
            {/* Executive Summary Header */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center border-b border-slate-800/80 pb-8">
              <div className="lg:col-span-2 space-y-4">
                <span className="px-3 py-1 bg-teal-500/10 border border-teal-500/20 text-teal-400 rounded-full text-xs font-bold tracking-wider uppercase">Value-Creation Alignment</span>
                <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white leading-tight">
                  Compressing Clinical Latency & Securing Sovereign Datasets
                </h2>
                <p className="text-slate-400 text-sm sm:text-base leading-relaxed">
                  In European Life Sciences, sourcing is the critical engine of <strong>clinical trial velocity, biological integrity, and sovereign regulatory resilience.</strong> When therapies rely on AI algorithms, real-world data training, and cold-chain logistics, procurement is no longer supportive—it defines the therapeutic asset itself.
                </p>
              </div>

              {/* Boardroom Mandate Box */}
              <div className="bg-slate-900 border border-slate-800 p-5 rounded-xl space-y-2 glow-teal">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Boardroom Mandate</h3>
                <p className="text-xs text-slate-300 leading-relaxed italic">
                  "AI-driven discovery compresses pre-clinical target timelines from years to six months. But if clinical site sourcing, cold-chain transport, or EU regulatory validations still take years, your computational ROI is lost."
                </p>
              </div>
            </div>

            {/* Shifting Value Frontiers Layout */}
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <span className="h-1.5 w-6 bg-teal-500 rounded-full"></span>
                <h3 className="text-sm font-bold uppercase tracking-widest text-slate-400">Strategic Context: Shifting Value Frontiers</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Shift 1 Card */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4 relative overflow-hidden flex flex-col justify-between">
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-teal-500/5 rounded-full blur-2xl"></div>
                  <div className="space-y-3">
                    <span className="text-[9px] font-bold text-teal-400 uppercase tracking-widest block">Strategic Shift 1</span>
                    <h4 className="text-base font-black text-white">The Discovery-to-Clinical Velocity Gap</h4>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      Generative biology and structural predictive models have compressed pre-clinical target discovery down to <strong>under six months</strong>. However, clinical execution still takes <strong>6 to 7 years</strong> due to site onboarding friction and static CRO bidding.
                    </p>
                  </div>

                  {/* Timeline visual representation */}
                  <div className="bg-slate-950 p-4 rounded-xl border border-slate-800/60 text-[10px] space-y-3 font-mono mt-4">
                    <div className="space-y-1">
                      <div className="flex justify-between text-[8px] text-slate-500">
                        <span>DISCOVERY (AI-DRIVEN)</span>
                        <span className="text-teal-400 font-bold">6 MO (COMPRESSED)</span>
                      </div>
                      <div className="w-full bg-slate-900 rounded-full h-1">
                        <div className="bg-teal-500 h-1 rounded-full" style={{ width: '10%' }}></div>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-[8px] text-slate-500">
                        <span>CLINICAL TRIALS (THE BOTTLENECK)</span>
                        <span className="text-rose-400 font-bold">6-7 YEARS</span>
                      </div>
                      <div className="w-full bg-slate-900 rounded-full h-1">
                        <div className="bg-rose-500 h-1 rounded-full" style={{ width: '100%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Shift 2 Card */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4 relative overflow-hidden flex flex-col justify-between">
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-indigo-500/5 rounded-full blur-2xl"></div>
                  <div className="space-y-3">
                    <span className="text-[9px] font-bold text-indigo-400 uppercase tracking-widest block">Strategic Shift 2</span>
                    <h4 className="text-base font-black text-white">"N-of-1" Biological Logistics</h4>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      Sourcing is moving away from bulk active chemistry toward Cell & Gene Therapies (CGT). Because personalized medicine has no bulk inventory, the biological supply chain must handle real-time cryogenic temperature telemetry inside rigid time loops.
                    </p>
                  </div>
                  <div className="bg-slate-950 p-3 rounded-xl border border-slate-800/60 flex items-center justify-between text-[10px] text-slate-400 mt-4">
                    <span><i className="fa-solid fa-temperature-arrow-down text-indigo-400 mr-1.5 animate-pulse"></i> Cryogenic Telemetry Loops</span>
                    <span className="font-bold text-indigo-400 font-mono">100% Real-Time</span>
                  </div>
                </div>

                {/* Shift 3 Card */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4 relative overflow-hidden flex flex-col justify-between">
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-rose-500/5 rounded-full blur-2xl"></div>
                  <div className="space-y-3">
                    <span className="text-[9px] font-bold text-rose-400 uppercase tracking-widest block">Strategic Shift 3</span>
                    <h4 className="text-base font-black text-white">From Patents to Proprietary Data Moats</h4>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      Molecules are easily bypassable by computational structural updates, shifting IP barriers to high-fidelity biological training data datasets. Sourcing this data requires absolute consent audit lineages under European GDPR, EHDS, and EU AI Act constraints.
                    </p>
                  </div>
                  <div className="bg-slate-950 p-3 rounded-xl border border-slate-800/60 flex items-center justify-between text-[10px] text-slate-400 mt-4">
                    <span><i className="fa-solid fa-gavel text-rose-400 mr-1.5"></i> EU AI Act & EHDS Audit</span>
                    <span className="font-bold text-rose-400 font-mono">Zero Liability Target</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Strategic Boardroom Recommendations */}
            <div className="pt-8 border-t border-slate-800/80 space-y-6">
              <div className="flex items-center gap-2">
                <span className="h-1.5 w-6 bg-emerald-500 rounded-full"></span>
                <h3 className="text-sm font-bold uppercase tracking-widest text-slate-400">Strategic Boardroom Recommendations</h3>
              </div>

              <div className="space-y-4">
                {/* Rec 1 */}
                <div className="bg-slate-900/40 border-l-4 border-l-teal-500 border border-slate-800 rounded-r-2xl p-6 flex flex-col md:flex-row gap-5 md:items-center hover:bg-slate-800/40 transition-all-300">
                  <div className="shrink-0 w-12 h-12 rounded-full bg-teal-500/10 border border-teal-500/30 text-teal-400 flex items-center justify-center text-xl shadow-[0_0_15px_rgba(45,212,191,0.15)]">
                    <i className="fa-solid fa-gauge-high"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white mb-1.5">Fund Clinical Speed, Not Just Discovery</h4>
                    <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                      Reallocate technology budgets away from generic IT and directly into operational clinical speed. Optimizing CRO selection and precursor sourcing yields massive cash-flow returns by compressing the time-to-market.
                    </p>
                  </div>
                </div>

                {/* Rec 2 */}
                <div className="bg-slate-900/40 border-l-4 border-l-indigo-500 border border-slate-800 rounded-r-2xl p-6 flex flex-col md:flex-row gap-5 md:items-center hover:bg-slate-800/40 transition-all-300">
                  <div className="shrink-0 w-12 h-12 rounded-full bg-indigo-500/10 border border-indigo-500/30 text-indigo-400 flex items-center justify-center text-xl shadow-[0_0_15px_rgba(99,102,241,0.15)]">
                    <i className="fa-solid fa-file-contract"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white mb-1.5">Audit Data Sourcing in Due Diligence</h4>
                    <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                      When executing a "buy vs. build" strategy for AI startups, due diligence must focus on data lineage. Models trained on non-compliant patient data are toxic legal liabilities under European EHDS/GDPR law.
                    </p>
                  </div>
                </div>

                {/* Rec 3 */}
                <div className="bg-slate-900/40 border-l-4 border-l-rose-500 border border-slate-800 rounded-r-2xl p-6 flex flex-col md:flex-row gap-5 md:items-center hover:bg-slate-800/40 transition-all-300">
                  <div className="shrink-0 w-12 h-12 rounded-full bg-rose-500/10 border border-rose-500/30 text-rose-400 flex items-center justify-center text-xl shadow-[0_0_15px_rgba(244,63,94,0.15)]">
                    <i className="fa-solid fa-shield-halved"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white mb-1.5">Position Procurement as Valuation Shield</h4>
                    <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                      Connect the sourcing transformation directly to the exit timeline. A fully audited, GxP-validated supply chain guarantees an exit premium by completely removing the buyer's regulatory integration risk.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Transition Call-To-Action to Tab 2 */}
            <div className="bg-slate-900/60 border border-slate-800 p-6 rounded-2xl flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="space-y-1">
                <h4 className="text-sm font-bold text-white">Consensus Formed? Ready to Map Your Portfolio?</h4>
                <p className="text-xs text-slate-400">Pivot to the next section to review the 4-layer architectural landscape.</p>
              </div>
              <button
                onClick={() => setActiveTab(2)}
                className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-xs font-bold uppercase tracking-wider rounded-xl shadow-lg shadow-cyan-500/20 transition-all-300 text-white flex items-center gap-2"
              >
                View the Sourcing Stack <i className="fa-solid fa-circle-arrow-right"></i>
              </button>
            </div>
          </section>

          {/* ==================== TAB 2 CONTENT: SOURCING STACK (THE "WHAT") ==================== */}
          <section className={sectionClass(2)}>
            {/* Market Map Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Left Column: The 4 Layers */}
              <div className="lg:col-span-8 space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <i className="fa-solid fa-network-wired text-teal-400"></i>
                    Sourcing Stack Architecture
                  </h3>
                  <span className="text-xs text-slate-500">Click a Layer or Player to inspect parameters</span>
                </div>

                {/* LAYER 1 */}
                <div onClick={() => selectLayer(1)} className={layerBoxClass(1)}>
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-teal-500"></div>
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-start">
                    <div className="sm:col-span-3 flex items-center gap-3">
                      <span className="flex items-center justify-center w-8 h-8 bg-teal-500/10 border border-teal-500/20 text-teal-400 font-extrabold rounded-lg text-sm">1</span>
                      <div>
                        <h4 className="text-xs font-black text-white group-hover:text-teal-400 transition-all-300 uppercase tracking-wider">Clinical Operations & Trial Intake</h4>
                        <span className="text-[10px] text-slate-500 font-medium">Compressing Trial Setup Lag</span>
                      </div>
                    </div>

                    {/* Sourcing Value Shift */}
                    <div className="sm:col-span-4 text-xs">
                      <span className="text-[9px] font-extrabold uppercase tracking-widest text-slate-500 block mb-1">Operational Shift</span>
                      <span className="text-slate-400 block"><i className="fa-solid fa-triangle-exclamation text-rose-500/70 mr-1"></i> Static CRO bidding with 9-month site contracting lag</span>
                      <span className="text-teal-400 block mt-1"><i className="fa-solid fa-circle-check text-teal-500 mr-1"></i> Pre-negotiated GxP MSAs and automated cohort matching</span>
                    </div>

                    {/* Representative Players */}
                    <div className="sm:col-span-5">
                      <span className="text-[9px] font-extrabold uppercase tracking-widest text-slate-500 block mb-1">Representative Players</span>
                      <div className="flex flex-wrap gap-2">
                        <PlayerChip playerKey="veeva" domain="veeva.com" letter="V" name="Veeva Systems" onSelect={selectPlayer} />
                        <PlayerChip playerKey="science37" domain="science37.com" letter="S" name="Science 37" onSelect={selectPlayer} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* LAYER 2 */}
                <div onClick={() => selectLayer(2)} className={layerBoxClass(2)}>
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-indigo-500"></div>
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-start">
                    <div className="sm:col-span-3 flex items-center gap-3">
                      <span className="flex items-center justify-center w-8 h-8 bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 font-extrabold rounded-lg text-sm">2</span>
                      <div>
                        <h4 className="text-xs font-black text-white group-hover:text-indigo-400 transition-all-300 uppercase tracking-wider">Sovereign API & Cold Chain Logistics</h4>
                        <span className="text-[10px] text-slate-500 font-medium">Nearshore Telemetry & Cold Chain</span>
                      </div>
                    </div>

                    {/* Sourcing Value Shift */}
                    <div className="sm:col-span-4 text-xs">
                      <span className="text-[9px] font-extrabold uppercase tracking-widest text-slate-500 block mb-1">Operational Shift</span>
                      <span className="text-slate-400 block"><i className="fa-solid fa-triangle-exclamation text-rose-500/70 mr-1"></i> Fragile temperature risks & heavy dependency on non-EU APIs</span>
                      <span className="text-indigo-400 block mt-1"><i className="fa-solid fa-circle-check text-indigo-500 mr-1"></i> Continuous cryogenic sensor telemetry & regional nearshore pipelines</span>
                    </div>

                    {/* Representative Players */}
                    <div className="sm:col-span-5">
                      <span className="text-[9px] font-extrabold uppercase tracking-widest text-slate-500 block mb-1">Representative Players</span>
                      <div className="flex flex-wrap gap-2">
                        <PlayerChip playerKey="tracelink" domain="tracelink.com" letter="T" name="TraceLink" onSelect={selectPlayer} />
                        <PlayerChip playerKey="cryoport" domain="cryoport.com" letter="C" name="Cryoport" onSelect={selectPlayer} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* LAYER 3 */}
                <div onClick={() => selectLayer(3)} className={layerBoxClass(3)}>
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-emerald-500"></div>
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-start">
                    <div className="sm:col-span-3 flex items-center gap-3">
                      <span className="flex items-center justify-center w-8 h-8 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-extrabold rounded-lg text-sm">3</span>
                      <div>
                        <h4 className="text-xs font-black text-white group-hover:text-emerald-400 transition-all-300 uppercase tracking-wider">Bio-Digital Bill of Materials (SDBOM)</h4>
                        <span className="text-[10px] text-slate-500 font-medium">Unifying Device & Software Keys</span>
                      </div>
                    </div>

                    {/* Sourcing Value Shift */}
                    <div className="sm:col-span-4 text-xs">
                      <span className="text-[9px] font-extrabold uppercase tracking-widest text-slate-500 block mb-1">Operational Shift</span>
                      <span className="text-slate-400 block"><i className="fa-solid fa-triangle-exclamation text-rose-500/70 mr-1"></i> Fragmented device purchasing disconnected from software licenses</span>
                      <span className="text-emerald-400 block mt-1"><i className="fa-solid fa-circle-check text-emerald-500 mr-1"></i> Software-defined Bill of Materials pairing hardware with active firmware</span>
                    </div>

                    {/* Representative Players */}
                    <div className="sm:col-span-5">
                      <span className="text-[9px] font-extrabold uppercase tracking-widest text-slate-500 block mb-1">Representative Players</span>
                      <div className="flex flex-wrap gap-2">
                        <PlayerChip playerKey="benchling" domain="benchling.com" letter="B" name="Benchling" onSelect={selectPlayer} />
                        <PlayerChip playerKey="scilife" domain="scilife.io" letter="S" name="Scilife" onSelect={selectPlayer} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* LAYER 4 */}
                <div onClick={() => selectLayer(4)} className={layerBoxClass(4)}>
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-rose-500"></div>
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-start">
                    <div className="sm:col-span-3 flex items-center gap-3">
                      <span className="flex items-center justify-center w-8 h-8 bg-rose-500/10 border border-rose-500/20 text-rose-400 font-extrabold rounded-lg text-sm">4</span>
                      <div>
                        <h4 className="text-xs font-black text-white group-hover:text-rose-400 transition-all-300 uppercase tracking-wider">Sovereign Data & Regulatory Governance</h4>
                        <span className="text-[10px] text-slate-500 font-medium">GDPR, EHDS & AI Act Safeguards</span>
                      </div>
                    </div>

                    {/* Sourcing Value Shift */}
                    <div className="sm:col-span-4 text-xs">
                      <span className="text-[9px] font-extrabold uppercase tracking-widest text-slate-500 block mb-1">Operational Shift</span>
                      <span className="text-slate-400 block"><i className="fa-solid fa-triangle-exclamation text-rose-500/70 mr-1"></i> Blind acquisition of unverified data cohorts triggering regulatory deletions</span>
                      <span className="text-rose-400 block mt-1"><i className="fa-solid fa-circle-check text-rose-500 mr-1"></i> Complete data-consent audit lineage & federated research loops</span>
                    </div>

                    {/* Representative Players */}
                    <div className="sm:col-span-5">
                      <span className="text-[9px] font-extrabold uppercase tracking-widest text-slate-500 block mb-1">Representative Players</span>
                      <div className="flex flex-wrap gap-2">
                        <PlayerChip playerKey="owkin" domain="owkin.com" letter="O" name="Owkin" onSelect={selectPlayer} />
                        <PlayerChip playerKey="scale" domain="scale.com" letter="S" name="Scale AI Health" onSelect={selectPlayer} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Detail Sidebar Panel */}
              <div className="lg:col-span-4 bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-6 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 text-teal-400 border-b border-slate-800 pb-3 mb-4">
                    <i className={`${detail.icon} text-lg text-teal-400`}></i>
                    <h3 className="text-xs font-bold uppercase tracking-wider">{detail.type}</h3>
                  </div>

                  {/* Detail Texts */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-extrabold text-white">{detail.title}</h4>
                      <p className="text-xs text-slate-400 mt-2 leading-relaxed">{detail.desc}</p>
                    </div>

                    {/* Validation Audit Box */}
                    <div className="bg-slate-950 p-3.5 border border-slate-800 rounded-lg space-y-1">
                      <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block">Strategic Pathway & Compliance Profile</span>
                      <p className="text-xs text-slate-300 leading-relaxed">{detail.regulatory}</p>
                    </div>
                  </div>
                </div>

                {/* Strategic Target Rating */}
                <div className="bg-teal-500/10 border border-teal-500/20 p-4 rounded-xl space-y-1.5">
                  <span className="text-[9px] font-bold uppercase tracking-wider text-teal-400 block">Sourcing Velocity Metric</span>
                  <div className="text-sm font-black text-white">{detail.stat}</div>
                </div>
              </div>
            </div>

            {/* Transition Call-To-Action to Tab 3 (Capability Taxonomy) */}
            <div className="bg-slate-900/60 border border-slate-800 p-6 rounded-2xl flex flex-col sm:flex-row justify-between items-center gap-4 mt-8">
              <div className="space-y-1">
                <h4 className="text-sm font-bold text-white">Understand the Sourcing Architecture? Let's Define the Capabilities.</h4>
                <p className="text-xs text-slate-400">Move directly to the capability taxonomy to review unboxed core operational and regulatory requirements by layer.</p>
              </div>
              <button
                onClick={() => setActiveTab(3)}
                className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-xs font-bold uppercase tracking-wider rounded-xl shadow-lg shadow-cyan-500/20 transition-all-300 text-white flex items-center gap-2 shrink-0"
              >
                Define Strategic Interventions <i className="fa-solid fa-layer-group"></i>
              </button>
            </div>
          </section>

          {/* ==================== TAB 3 CONTENT: CAPABILITY TAXONOMY (THE DEFINITIONS) ==================== */}
          <section className={sectionClass(3)}>
            <div className="space-y-4 border-b border-slate-800/80 pb-6">
              <span className="px-3 py-1 bg-teal-500/10 border border-teal-500/20 text-teal-400 rounded-full text-xs font-bold tracking-wider uppercase">Target Operating Model</span>
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white leading-tight">
                Clinical Sourcing Interventions & Maturity Roadmap
              </h2>
              <p className="text-slate-400 text-sm sm:text-base leading-relaxed max-w-4xl">
                Define your Target Operating Model before evaluating specific vendors. This roadmap isolates the nine core interventions required to secure and accelerate the clinical supply chain. Use the lenses below to instantly filter by defensive pain points, offensive innovation vectors, and deployment maturity.
              </p>
            </div>

            {/* Interactive Strategic Thesis Filters */}
            <div className="bg-[#111424] border border-[#23283b] p-5 rounded-2xl space-y-5">
              {/* 1. Pain Point Filters (Defense) */}
              <div className="space-y-2.5">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><i className="fa-solid fa-triangle-exclamation text-rose-500/70"></i> Defense: Pain Point Lenses</span>
                <div className="flex flex-wrap gap-2.5">
                  {CAP_FILTERS_DEFENSE.map((f) => (
                    <button key={f.tag} onClick={() => toggleCapFilter(f.tag)} className={capFilterBtnClass(f)}>
                      <i className={f.icon}></i> {f.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="w-full h-px bg-[#23283b]/60"></div>

              {/* 2. Innovation Vector Filters (Offense) */}
              <div className="space-y-2.5">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><i className="fa-solid fa-bolt text-indigo-400/80"></i> Offense: Innovation Vectors</span>
                <div className="flex flex-wrap gap-2.5 items-center">
                  {CAP_FILTERS_OFFENSE.map((f) => (
                    <button key={f.tag} onClick={() => toggleCapFilter(f.tag)} className={capFilterBtnClass(f)}>
                      <i className={f.icon}></i> {f.label}
                    </button>
                  ))}
                  <button
                    onClick={() => setCapFilter('ALL')}
                    className={`flex items-center gap-2 px-3.5 py-2 ${capFilter === 'ALL' ? 'bg-teal-500/15 border-2 border-teal-500 shadow-lg shadow-teal-500/5' : 'bg-teal-500/10 border border-teal-500'} text-teal-400 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all ml-auto`}
                  >
                    <i className="fa-solid fa-arrows-rotate"></i> Reset Theme
                  </button>
                </div>
              </div>

              <div className="w-full h-px bg-[#23283b]/60"></div>

              {/* 3. Maturity Phase Filters */}
              <div className="space-y-2.5">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><i className="fa-solid fa-chart-bar text-slate-600"></i> Maturity Lens (Filter by Lifecycle Phase)</span>
                <div className="flex flex-wrap gap-2.5">
                  {MATURITY_FILTERS.map((f) => (
                    <button key={f.val} onClick={() => toggleMaturityFilter(f.val)} className={maturityFilterBtnClass(f)}>
                      <i className={f.icon}></i> {f.label}
                    </button>
                  ))}
                  <button
                    onClick={() => setMaturityFilter('ALL')}
                    className={`flex items-center gap-2 px-3.5 py-2 ${maturityFilter === 'ALL' ? 'bg-violet-500/15 border-2 border-violet-500 shadow-lg shadow-violet-500/5' : 'bg-violet-500/10 border border-violet-500'} text-violet-400 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all ml-auto`}
                  >
                    <i className="fa-solid fa-arrows-rotate"></i> Reset Maturity
                  </button>
                </div>
              </div>
            </div>

            {/* Capability Taxonomy Layers */}
            <div className="space-y-6">
              {CAP_LAYERS.map((cl) => (
                <div key={cl.layer} className={`bg-slate-900 border border-slate-800/80 rounded-2xl p-6 relative overflow-hidden border-l-4 ${cl.borderL} shadow-lg ${cl.shadow}`}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg ${cl.numCls.split(' ')[0]} border ${cl.numCls.split(' ').slice(1).join(' ')} flex items-center justify-center text-sm font-bold shadow-inner shrink-0`}>{cl.layer}</div>
                      <div>
                        <h3 className={`text-sm font-black uppercase tracking-wider ${cl.titleCls}`}>{cl.title}</h3>
                        <span className="text-[10px] text-slate-500 font-semibold block uppercase">{cl.subtitle}</span>
                      </div>
                    </div>
                  </div>

                  {/* Strategic Interventions Grid */}
                  <div className={`grid grid-cols-1 ${cl.cols} gap-5`}>
                    {cl.cards.map((card) => (
                      <div key={card.title} className={capCardClass(card, cl.layer)}>
                        <h4 className="text-sm font-bold text-slate-200 group-hover:text-white tracking-wide mb-3 leading-snug">{card.title}</h4>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {card.tags.map((tag) => (
                            <span key={tag} className={`px-2 py-1 rounded text-[9px] font-bold ${TAG_CHIP[tag].cls} border uppercase tracking-widest`}>{TAG_CHIP[tag].label}</span>
                          ))}
                          <span className={`px-2 py-1 rounded text-[9px] font-bold ${MATURITY_CHIP[card.maturity].cls} border uppercase tracking-widest font-mono ml-auto`}>{MATURITY_CHIP[card.maturity].label}</span>
                        </div>
                        <p className="text-xs text-slate-400 leading-relaxed mt-auto">{card.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Transition Call-To-Action to Tab 4 (Pipeline Screener / Target Discovery) */}
            <div className="bg-slate-900/60 border border-slate-800 p-6 rounded-2xl flex flex-col sm:flex-row justify-between items-center gap-4 mt-8">
              <div className="space-y-1">
                <h4 className="text-sm font-bold text-white">Capabilities Aligned? Let's Locate the Market Providers.</h4>
                <p className="text-xs text-slate-400">Advance to the Target Discovery Engine to screen Insurgents vs. Defenders across the ecosystem map.</p>
              </div>
              <button
                onClick={() => setActiveTab(4)}
                className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-xs font-bold uppercase tracking-wider rounded-xl shadow-lg shadow-cyan-500/20 transition-all-300 text-white flex items-center gap-2 shrink-0"
              >
                Launch Target Discovery <i className="fa-solid fa-radar"></i>
              </button>
            </div>
          </section>

          {/* ==================== TAB 4 CONTENT: TARGET DISCOVERY ENGINE (ECOSYSTEM MAP) ==================== */}
          <section className={sectionClass(4)}>
            {/* Header */}
            <div>
              <h2 className="text-2xl font-black tracking-tight text-white flex items-center gap-3">
                <i className="fa-solid fa-crosshairs text-teal-500"></i> Target Discovery Engine
              </h2>
              <p className="text-slate-400 text-sm mt-1">Use firmographic and strategic filters to isolate specific M&A targets, competitive threats, and high-growth disruptors within the sourcing ecosystem.</p>
            </div>

            {/* Master Filter Panel */}
            <div className="bg-[#111424] border border-[#23283b] rounded-2xl p-5 space-y-6">
              {/* Firmographic Selectors */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {DISC_FILTER_GROUPS.map((g) => (
                  <div key={g.key} className="space-y-3">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><i className={`${g.icon} text-slate-600`}></i> {g.label}</span>
                    <div className="flex flex-wrap gap-1.5 bg-[#0b0e17] p-1.5 rounded-lg border border-[#23283b]">
                      {g.options.map((opt) => (
                        <button key={opt} onClick={() => setDiscFilter(g.key, opt)} className={discBtnClass(g.key, opt)}>
                          {g.labels[opt]}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Match Counter */}
            <div className="flex items-center gap-3 py-2">
              <span className="px-3 py-1.5 bg-slate-800 border border-slate-700 text-white text-[10px] font-bold uppercase tracking-widest rounded-md flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span> {matchCount} MATCHING TARGETS
              </span>
            </div>

            {/* Discovery Layers */}
            {DISCOVERY_LAYERS.map((dl) => (
              <div key={dl.id} className="bg-[#111520] border border-[#1e2538] rounded-2xl p-6 relative overflow-hidden mt-6">
                <div className="flex items-center gap-3 mb-6 border-b border-slate-800 pb-4">
                  <div className={`w-8 h-8 rounded-lg ${dl.chipCls} border flex items-center justify-center text-xs font-black shadow-lg shrink-0`}>{dl.id}</div>
                  <h3 className="text-base font-bold text-white">{dl.title}</h3>
                </div>

                <div className={dl.grid}>
                  {dl.segments.map((seg) => (
                    <div key={seg.title} className="border border-slate-800/60 rounded-xl p-5 bg-slate-900/30 flex flex-col h-full">
                      <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2"><span className={`w-1.5 h-1.5 ${dl.dotCls} rounded-full shrink-0`}></span> {seg.title}</h4>
                      <div className="flex flex-col gap-6 flex-1">
                        <div className="space-y-2.5">
                          <span className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest">Market Defenders</span>
                          <div className="flex flex-wrap gap-2.5">
                            {seg.defenders.map((t) => (
                              <TargetChip key={`${seg.title}-${t.name}`} t={{ ...t, type: 'defender' }} layerColor={dl.color} visible={matchTarget(t)} />
                            ))}
                          </div>
                        </div>
                        <div className="space-y-2.5 mt-auto">
                          <span className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest">AI Insurgents</span>
                          <div className="flex flex-wrap gap-2.5">
                            {seg.insurgents.map((t) => (
                              <TargetChip key={`${seg.title}-${t.name}`} t={{ ...t, type: 'insurgent' }} layerColor={dl.color} visible={matchTarget(t)} />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Transition Call-To-Action to Tab 5 */}
            <div className="bg-slate-900/60 border border-slate-800 p-6 rounded-2xl flex flex-col sm:flex-row justify-between items-center gap-4 mt-8">
              <div className="space-y-1">
                <h4 className="text-sm font-bold text-white">Targets Identified. How Do We Execute?</h4>
                <p className="text-xs text-slate-400">Advance to the final section to review the DJA Frontier Full Lifecycle Co-Execution model.</p>
              </div>
              <button
                onClick={() => setActiveTab(5)}
                className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-xs font-bold uppercase tracking-wider rounded-xl shadow-lg shadow-cyan-500/20 transition-all-300 text-white flex items-center gap-2 shrink-0"
              >
                View Execution Playbook <i className="fa-solid fa-infinity"></i>
              </button>
            </div>
          </section>

          {/* ==================== TAB 5 CONTENT: CO-EXECUTION MODEL ==================== */}
          <section className={sectionClass(5)}>
            {/* Header */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center border-b border-slate-800/80 pb-8">
              <div className="lg:col-span-2 space-y-4">
                <span className="px-3 py-1 bg-purple-500/10 border border-purple-500/20 text-purple-400 rounded-full text-xs font-bold tracking-wider uppercase">Agentic Alpha Foundry</span>
                <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white leading-tight">
                  Full Lifecycle Co-Execution & Value Realization
                </h2>
                <p className="text-slate-400 text-sm sm:text-base leading-relaxed">
                  Most strategy firms hand over an expensive slide deck of recommendations and walk away. <strong>DJA Frontier operates as a co-execution partner.</strong> We don't just advise; we underwrite high-conviction AI theses and embed alongside your leadership team to manufacture structural advantages end-to-end.
                </p>
              </div>

              {/* Value Prop Box */}
              <div className="bg-slate-900 border border-slate-800 p-5 rounded-xl space-y-2 glow-purple">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">The DJA Frontier Promise</h3>
                <p className="text-xs text-slate-300 leading-relaxed italic">
                  "We stand shoulder-to-shoulder with corporate sponsors. From initial tech-stack due diligence to deploying agentic guardrails and scaling for a premium exit, we are your skin-in-the-game transformation engineers."
                </p>
              </div>
            </div>

            {/* 3 Phase Lifecycle */}
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <span className="h-1.5 w-6 bg-purple-500 rounded-full"></span>
                <h3 className="text-sm font-bold uppercase tracking-widest text-slate-400">The 3-Phase Execution Methodology</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Phase 1 */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 relative overflow-hidden flex flex-col group hover:border-cyan-500/40 transition-all-300">
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-cyan-500/5 rounded-full blur-2xl"></div>
                  <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 flex items-center justify-center text-lg mb-5 shadow-inner">
                    <i className="fa-solid fa-microscope"></i>
                  </div>
                  <h4 className="text-base font-black text-white mb-2">Phase 1: Investment & Underwriting</h4>
                  <p className="text-xs text-slate-400 leading-relaxed flex-grow">
                    We fuse deep C-suite domain expertise with Frontier AI capabilities to precisely define your investment thesis. We run rigorous tech-stack and compliance due diligence on prospective targets from the ecosystem to ensure alignment with EHDS and AI Act guardrails.
                  </p>
                  <div className="mt-4 pt-4 border-t border-slate-800/60 text-[10px] font-bold text-cyan-400 uppercase tracking-widest">
                    Target Output: Validated M&A Thesis
                  </div>
                </div>

                {/* Phase 2 */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 relative overflow-hidden flex flex-col group hover:border-indigo-500/40 transition-all-300">
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-indigo-500/5 rounded-full blur-2xl"></div>
                  <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 flex items-center justify-center text-lg mb-5 shadow-inner">
                    <i className="fa-solid fa-network-wired"></i>
                  </div>
                  <h4 className="text-base font-black text-white mb-2">Phase 2: Transformation Orchestration</h4>
                  <p className="text-xs text-slate-400 leading-relaxed flex-grow">
                    Post-acquisition or partnership, we embed directly with your operational teams. We build the physical-digital threads (SDBOM), deploy agentic sourcing guardrails, and structurally engineer the automated clinical speed advantages we underwrote in Phase 1.
                  </p>
                  <div className="mt-4 pt-4 border-t border-slate-800/60 text-[10px] font-bold text-indigo-400 uppercase tracking-widest">
                    Target Output: Operational Alpha
                  </div>
                </div>

                {/* Phase 3 */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 relative overflow-hidden flex flex-col group hover:border-purple-500/40 transition-all-300">
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-purple-500/5 rounded-full blur-2xl"></div>
                  <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-400 flex items-center justify-center text-lg mb-5 shadow-inner">
                    <i className="fa-solid fa-chart-line"></i>
                  </div>
                  <h4 className="text-base font-black text-white mb-2">Phase 3: Strategic Exit Preparation</h4>
                  <p className="text-xs text-slate-400 leading-relaxed flex-grow">
                    We manage the lifecycle through to ultimate value realization. By ensuring the technology infrastructure and data sovereign lineages are fully audited and scalable, we remove buyer integration risk, allowing the asset to command a massive premium multiplier at exit.
                  </p>
                  <div className="mt-4 pt-4 border-t border-slate-800/60 text-[10px] font-bold text-purple-400 uppercase tracking-widest">
                    Target Output: Premium Valuation Multiplier
                  </div>
                </div>
              </div>
            </div>

            {/* Contact & Leadership CTA */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
              {/* Call to Action */}
              <div className="lg:col-span-2 bg-gradient-to-r from-slate-900 to-slate-950 border border-slate-800 p-8 rounded-2xl space-y-5 relative overflow-hidden flex flex-col justify-center">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L3N2Zz4=')] [mask-image:linear-gradient(to_bottom,white,transparent)]"></div>
                <h3 className="text-2xl sm:text-3xl font-black text-white relative z-10 leading-tight">Ready to Build the Sourcing Engine of the Future?</h3>
                <p className="text-sm text-slate-400 max-w-xl relative z-10 leading-relaxed">Connect with the DJA Frontier partnership team to initiate a custom technology underwriting diagnostic for your portfolio.</p>
                <div className="pt-2 relative z-10">
                  <button className="px-8 py-3.5 bg-white text-slate-950 hover:bg-slate-200 text-sm font-bold uppercase tracking-widest rounded-xl shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all-300">
                    Initiate Strategic Dialogue
                  </button>
                </div>
              </div>

              {/* Leadership Profile Card */}
              <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl relative overflow-hidden flex flex-col items-center text-center justify-center group hover:border-purple-500/40 transition-all-300">
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl group-hover:bg-purple-500/20 transition-all-300"></div>

                <div className="relative mb-5 mt-2">
                  <div className="absolute inset-0 rounded-full bg-purple-500/20 blur-md animate-pulse"></div>
                  <img
                    src="\images\profile_bharat.png"
                    alt="Dr. Bharat Madan"
                    className="relative w-20 h-20 rounded-full border-2 border-purple-500/50 object-cover z-10 shadow-lg"
                  />
                </div>

                <div className="space-y-1.5 relative z-10">
                  <h4 className="text-lg font-black text-white tracking-tight">Dr. Bharat Madan</h4>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-purple-400 block">Managing Partner, DJA Frontier</span>
                </div>

                <div className="flex items-center justify-center gap-4 mt-5 pt-4 border-t border-slate-800/80 w-full relative z-10">
                  <span className="flex items-center gap-1.5 text-xs text-slate-300 font-medium">
                    <i className="fa-solid fa-location-dot text-slate-500"></i> Luxembourg
                  </span>
                  <span className="w-px h-3 bg-slate-700"></span>
                  <a href="https://www.linkedin.com/in/bharatmadan1/" target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-white transition-colors font-medium">
                    <i className="fa-brands fa-linkedin text-[#0A66C2] text-sm"></i> Connect
                  </a>
                </div>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="mt-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-xs text-slate-600 border-t border-slate-900 pt-8">
          <p>© 2026 DJA Frontier IP. Life Sciences Strategic Decision Engine.</p>
        </footer>
      </div>
    </div>
  );
}
